package com.sddamico.swigmore.models

import kotlinx.serialization.Serializable

// Times are epoch milliseconds.

@Serializable
enum class WeekStatus { OPEN, CLOSED, PUBLISHED }

@Serializable
enum class BallotAction { SUBMITTED, AMENDED, WITHDRAWN }

@Serializable
enum class BallotStatus { ON_TIME, AMENDED, LATE }

@Serializable
data class PollTeamDto(
  val id: String,
  val name: String,
  val shortName: String?,
  val ownerName: String?,
  val record: TeamRecord?,
)

@Serializable
data class PollWeekDto(
  val season: Int,
  val week: Int,
  val status: WeekStatus,
  val closesAt: Long,
  val publishedAt: Long?,
)

@Serializable
data class MyBallotDto(
  val teamIds: List<String>,
  val status: BallotStatus,
  val submittedAt: Long,
  val updatedAt: Long,
)

/**
 * Home + ballot screen state. [week] is null before the commissioner opens the first poll.
 * [canVote] is whether the caller is a league member, and [myTeamId] the Fantrax team they own;
 * [myBallot] is theirs for [week]. [lastWeek] maps team id to its standing in the season's poll
 * before [week], null when there is none; a team on no ballot is absent. [previousBallot] is the caller's
 * ballot in that poll, if they cast one. [hasPublishedPoll] is whether any poll has been published,
 * so home knows whether to ask for results. [recap] is the write-up of the games before [week], once
 * Claude has written it, until [week]'s poll publishes.
 * [canReviewBallot] is whether the server can review a ballot with Claude.
 */
@Serializable
data class CurrentPollResponse(
  val week: PollWeekDto?,
  val ballotCount: Int,
  val memberCount: Int,
  val canVote: Boolean,
  val myTeamId: String?,
  val myBallot: MyBallotDto?,
  val teams: List<PollTeamDto>,
  val lastWeek: Map<String, PollStanding>?,
  val previousBallot: PreviousBallotDto?,
  val hasPublishedPoll: Boolean,
  val recap: WeekendRecapDto?,
  val canReviewBallot: Boolean,
)

/** An owner's ballot in poll [week], in their order, with how each team did in that week's games. */
@Serializable
data class PreviousBallotDto(val week: Int, val picks: List<PreviousPickDto>)

/** [game] is null when the team has no scored game that week. */
@Serializable
data class PreviousPickDto(val teamId: String, val game: PickGameDto?)

/** [final] is false while Fantrax can still correct the scores. */
@Serializable
data class PickGameDto(
  val opponentId: String,
  val score: Double,
  val opponentScore: Double,
  val outcome: GameOutcome,
  val final: Boolean,
)

/** The recap of week [gamesWeek]'s games. */
@Serializable
data class WeekendRecapDto(val gamesWeek: Int, val headline: String, val paragraphs: List<String>)

/** [previousStanding] is the team's standing in the previous published poll this season. */
@Serializable
data class TeamTallyDto(
  val team: PollTeamDto,
  val rank: Int,
  val tied: Boolean,
  val points: Int,
  val firstPlaceVotes: Int,
  /** Ballots that ranked this team at all. */
  val onBallots: Int,
  /** Votes at each ballot position: index 0 is 1st place. */
  val votesByRank: List<Int>,
  val avgRank: Double,
  val highRank: Int,
  val lowRank: Int,
  val previousStanding: PollStanding,
)

@Serializable
data class PollResultsResponse(
  val week: PollWeekDto,
  val ballotCount: Int,
  val memberCount: Int,
  val ranked: List<TeamTallyDto>,
  val alsoReceiving: List<TeamTallyDto>,
  /** The season's first poll, so no team has a last week. */
  val firstPoll: Boolean,
  /** The published headline and intro; only `/api/poll/results` sets it, for a published week. */
  val announcement: PollAnnouncementDto?,
)

@Serializable
data class PollAnnouncementDto(val headline: String, val paragraphs: List<String>)

@Serializable
data class TeamRankHistoryDto(
  val team: PollTeamDto,
  /** Standing per week, in [PollArchiveResponse.weeks] order. */
  val standings: List<PollStanding>,
)

@Serializable
data class PollArchiveResponse(
  val season: Int?,
  /** Seasons with a published poll, newest first. */
  val seasons: List<Int>,
  val weeks: List<PollResultsResponse>,
  val history: List<TeamRankHistoryDto>,
)

@Serializable
data class SubmitBallotRequest(val teamIds: List<String>)

/** The ballot as it stands on screen: [slots] in order, null where a slot is empty. */
@Serializable
data class BallotReviewRequest(val slots: List<String?>)

/** What a review note checks the ballot against. */
@Serializable
enum class ReviewNoteKind {
  /** The owner's own ballot last week. */
  CHANGE,
  /** The computer polls and last week's coaches poll. */
  OUTLIER,
  /** Game results and head-to-head. */
  RESULT,
}

@Serializable
data class BallotReviewNoteDto(val kind: ReviewNoteKind, val text: String)

@Serializable
data class BallotReviewResponse(val summary: String, val notes: List<BallotReviewNoteDto>)

@Serializable
data class MemberDto(val email: String, val displayName: String, val teamId: String?)

@Serializable
data class AdminBallotDto(
  val id: String,
  val voter: MemberDto,
  val teamIds: List<String>,
  val status: BallotStatus,
  /** The commissioner who entered or last edited it, if any. */
  val enteredBy: String?,
  val submittedAt: Long,
  val updatedAt: Long,
)

@Serializable
data class ActivityDto(
  val voter: MemberDto,
  val action: BallotAction,
  /** The commissioner's email when they made the change. */
  val actorEmail: String?,
  val at: Long,
)

@Serializable
data class AdminBallotsResponse(
  /** Null until the first poll is opened. */
  val week: PollWeekDto?,
  val ballots: List<AdminBallotDto>,
  val notVoted: List<MemberDto>,
  /** Newest first. */
  val activity: List<ActivityDto>,
  /** The ballot pool with records going into [week]. */
  val teams: List<PollTeamDto>,
  /** The Fantrax week the commissioner can open now, if any. */
  val openableWeek: Int?,
  /** [week] is published and its ballots have changed since, so republishing would change the poll. */
  val liveTallyChanged: Boolean,
  /** Whether a week's announcement goes out when its poll publishes, or waits for the commissioner. */
  val autoSend: Boolean,
  /** [week]'s announcement email and push have gone out. */
  val announcementSent: Boolean,
)

@Serializable
data class WeekRequest(val season: Int, val week: Int)

@Serializable
data class AutoSendRequest(val autoSend: Boolean)

/** A week of the current season. [closesAt] (epoch ms) defaults to the next Friday 11:00 PM ET. */
@Serializable
data class OpenWeekRequest(val week: Int, val closesAt: Long? = null)

/** [late] defaults to whether a new ballot arrives after the deadline; edits keep the flag. */
@Serializable
data class AdminSaveBallotRequest(
  val season: Int,
  val week: Int,
  val voterEmail: String,
  val teamIds: List<String>,
  val late: Boolean? = null,
)

@Serializable
data class BallotIdRequest(val ballotId: String)

@Serializable
data class EmailRequest(val email: String)

@Serializable
data class MembersResponse(val members: List<MemberDto>)

/** [previousEmail] is set when the commissioner changes an existing member's email. */
@Serializable
data class SaveMemberRequest(val email: String, val displayName: String, val teamId: String?, val previousEmail: String? = null)
